import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { EvaluationContext } from "../../context/EvaluationContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { useMutualUnread } from "../../context/MutualUnreadContext";
import { useParticipantRequestUnread } from "../../context/ParticipantRequestUnreadContext";
import { useWaitlistUnread } from "../../context/WaitlistUnreadContext";
import AppModalShell, {
  AppModalButton,
  APP_MODAL_BODY_TEXT_CLASS,
} from "../common/AppModalShell";
import EventCard from "./EventCard";

const DISMISS_KEY_PREFIX = "vg-host-capacity-upsell-dismissed";
const CREATED_EVENTS_PAGE_SIZE = 50;
const NEAR_FULL_REMAINING_THRESHOLD = 2;
const REFRESH_INTERVAL_MS = 120_000;
const INACTIVE_EVENT_STATUSES = new Set([
  "cancelled",
  "canceled",
  "deleted",
  "archived",
  "finished",
  "completed",
]);
const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FREE_CAPACITY_LIMIT = 15;
const PLUS_CAPACITY_LIMIT = 30;
const PREMIUM_CAPACITY_LIMIT = 50;

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toPositiveInt = (value, fallback = 0) => {
  const parsed = Math.floor(toFiniteNumber(value, fallback));
  return parsed > 0 ? parsed : fallback;
};

const normalizeSubscriptionTier = (hostRating) => {
  const explicitTier = String(hostRating?.subscription_tier || "").trim().toLowerCase();
  if (explicitTier === "free" || explicitTier === "plus" || explicitTier === "premium") {
    return explicitTier;
  }
  if (hostRating?.has_premium_subscription) return "premium";
  if (hostRating?.has_plus_access) return "plus";
  return "free";
};

const resolveHostCapacityLimits = () => {
  const freeLimit = FREE_CAPACITY_LIMIT;
  const plusLimit = PLUS_CAPACITY_LIMIT;
  const premiumLimit = PREMIUM_CAPACITY_LIMIT;

  return { freeLimit, plusLimit, premiumLimit };
};

const getParticipantCount = (event) => {
  const explicitCount = toFiniteNumber(event?.participants_count, NaN);
  if (Number.isFinite(explicitCount) && explicitCount >= 0) {
    return Math.floor(explicitCount);
  }
  if (Array.isArray(event?.participants)) {
    return event.participants.length;
  }
  return 0;
};

const getEventName = (event) =>
  String(event?.name || event?.title || event?.event_name || "").trim();

const getEventId = (event) => event?.id ?? event?.event_id ?? null;

const pad2 = (value) => String(value).padStart(2, "0");

const formatEventDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "");
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())} ${pad2(date.getDate())}/${pad2(
    date.getMonth() + 1
  )} ${WEEKDAY_ABBR[date.getDay()]}`;
};

const isInactiveEvent = (event) => {
  const status = String(event?.status || "").trim().toLowerCase();
  return INACTIVE_EVENT_STATUSES.has(status);
};

const isHostedEvent = (event) => {
  const hostFlag =
    event?.is_host ?? event?.viewer_is_host ?? event?.i_am_host ?? event?.is_creator;
  return hostFlag !== false;
};

const buildDismissKey = (prompt) =>
  `${DISMISS_KEY_PREFIX}:${prompt.eventId}:${prompt.stage}:${prompt.capacity}`;

export const resolveHostCapacityUpsellPrompt = ({ events = [], hostRating = null } = {}) => {
  if (normalizeSubscriptionTier(hostRating) !== "free") {
    return null;
  }

  const { freeLimit, plusLimit, premiumLimit } = resolveHostCapacityLimits(hostRating);
  const candidates = events
    .map((event) => {
      const eventId = getEventId(event);
      const capacity = toPositiveInt(event?.max_participants, 0);
      if (!eventId || capacity !== freeLimit || isInactiveEvent(event) || !isHostedEvent(event)) {
        return null;
      }
      if (event?.is_sandbox || event?.sandbox_event) {
        return null;
      }

      const participants = getParticipantCount(event);
      const remaining = Math.max(0, capacity - participants);
      const stage =
        participants >= capacity
          ? "full"
          : remaining > 0 && remaining <= NEAR_FULL_REMAINING_THRESHOLD
          ? "near_full"
          : "";

      if (!stage) return null;

      return {
        eventId,
        eventName: getEventName(event),
        event,
        participants,
        capacity,
        remaining,
        stage,
        freeLimit,
        plusLimit,
        premiumLimit,
        targetTier: plusLimit > capacity ? "plus" : "premium",
      };
    })
    .filter(Boolean);

  candidates.sort((a, b) => {
    if (a.stage !== b.stage) return a.stage === "full" ? -1 : 1;
    if (a.remaining !== b.remaining) return a.remaining - b.remaining;
    if (b.participants !== a.participants) return b.participants - a.participants;
    return Number(b.eventId) - Number(a.eventId);
  });

  return candidates[0] || null;
};

const fetchHostCapacityPromptData = async () => {
  const [eventsRes, hostRatingRes] = await Promise.all([
    api.get("/events/events_packs/", {
      params: {
        type: "created",
        page: 1,
        page_size: CREATED_EVENTS_PAGE_SIZE,
      },
      meta: { suppressGlobalError: true },
      suppressUnauthorizedRedirect: true,
    }),
    api.get("/events/get-host-rating/", {
      meta: { suppressGlobalError: true },
      suppressUnauthorizedRedirect: true,
    }),
  ]);

  return {
    events: Array.isArray(eventsRes?.data?.results)
      ? eventsRes.data.results
      : Array.isArray(eventsRes?.data?.events)
      ? eventsRes.data.events
      : [],
    hostRating: hostRatingRes?.data || null,
  };
};

function CapacityProgress({ prompt, t }) {
  const remaining = Math.max(0, prompt.capacity - prompt.participants);
  const progressPercent = prompt.capacity
    ? Math.min(100, Math.max(0, (prompt.participants / prompt.capacity) * 100))
    : 0;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.035] px-3.5 py-3">
      <div className="flex items-center justify-between gap-3 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[#9fc2ff]">
        <span>
          {t("host_capacity_prompt.event_card_capacity", {
            defaultValue: "{{participants}} / {{capacity}} joined",
            participants: prompt.participants,
            capacity: prompt.capacity,
          })}
        </span>
        <span>
          {remaining > 0
            ? t("host_capacity_prompt.remaining_spots", {
                defaultValue: "{{count}} left",
                count: remaining,
              })
            : t("host_capacity_prompt.full_badge", { defaultValue: "Full" })}
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/12">
        <div
          className="h-full rounded-full bg-[#9fc2ff]"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}

function EventCardPreview({ prompt, eventName }) {
  const event = prompt?.event || {};
  const datetime = formatEventDate(
    event?.date_time || event?.datetime || event?.start_time || event?.date
  );

  return (
    <div className="rounded-2xl">
      <EventCard
        title={eventName}
        type={event?.type}
        isGhostConcept={Boolean(event?.is_hostless_concept)}
        isOnline={Number(event?.event_format) === 0}
        isPersonal={Number(event?.host_format) === 0}
        isPaid={Number(event?.fee_type) === 1}
        isByRequest={Boolean(event?.join_by_request)}
        minVaultLevel={event?.min_vault_level}
        isVerified={event?.is_verified}
        isValidated={event?.is_validated}
        datetime={datetime}
        isFavourite={false}
        isSelected={false}
        fromMap={false}
        onToggleFavourite={() => {}}
        status={event?.status}
        badgeLabel={event?.badge_label}
        iconImageUrl={event?.icon_image_url}
        isPromoted={Boolean(event?.is_promoted_active ?? event?.is_promoted ?? event?.promoted)}
        isPlatformEvent={Boolean(event?.is_platform_event)}
        platformAccentIndex={0}
        showFavouriteButton={false}
        searchListLayout
      />
    </div>
  );
}

function CapacityStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
      <div className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-white/45">
        {label}
      </div>
      <div className="mt-2 text-[1.2rem] font-semibold leading-none text-white">
        {value}
      </div>
    </div>
  );
}

export default function HostCapacityUpsellPromptModal({
  promptOverride = null,
  persistDismissal = true,
  onDismiss = null,
  onSubscribeOverride = null,
}) {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const location = useLocation();
  const requiredActionsCtx = useContext(RequiredActionsContext);
  const evaluationCtx = useContext(EvaluationContext);
  const mutualUnreadCtx = useMutualUnread();
  const waitlistUnreadCtx = useWaitlistUnread();
  const participantRequestUnreadCtx = useParticipantRequestUnread();
  const [dismissedPromptKey, setDismissedPromptKey] = useState("");
  const queryEnabled = !promptOverride;

  const { data } = useQuery({
    queryKey: ["host_capacity_upsell_prompt"],
    enabled: queryEnabled,
    queryFn: fetchHostCapacityPromptData,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const resolvedPrompt = useMemo(
    () => promptOverride || resolveHostCapacityUpsellPrompt(data || {}),
    [data, promptOverride]
  );
  const promptKey = resolvedPrompt ? buildDismissKey(resolvedPrompt) : "";
  const storageDismissed =
    persistDismissal &&
    promptKey &&
    typeof window !== "undefined" &&
    window.localStorage.getItem(promptKey) === "1";
  const isDismissed = Boolean(promptKey && (dismissedPromptKey === promptKey || storageDismissed));
  const higherPriorityOpen = Boolean(
    requiredActionsCtx?.open ||
      evaluationCtx?.open ||
      evaluationCtx?.hasBlocking ||
      Number(mutualUnreadCtx?.unreadCount || 0) > 0 ||
      Number(waitlistUnreadCtx?.totalUnread || 0) > 0 ||
      Number(participantRequestUnreadCtx?.totalUnread || 0) > 0
  );
  const isSubscriptionRoute = location.pathname === "/menu/subscription";
  const visible = Boolean(
    resolvedPrompt &&
      promptKey &&
      !isDismissed &&
      (!higherPriorityOpen || promptOverride) &&
      (!isSubscriptionRoute || promptOverride)
  );

  const markDismissed = React.useCallback(() => {
    if (!promptKey) return;
    if (persistDismissal && typeof window !== "undefined") {
      window.localStorage.setItem(promptKey, "1");
    }
    setDismissedPromptKey(promptKey);
  }, [persistDismissal, promptKey]);

  const handleClose = React.useCallback(() => {
    markDismissed();
    onDismiss?.();
  }, [markDismissed, onDismiss]);

  const handleSubscribe = React.useCallback(() => {
    if (!resolvedPrompt) return;
    markDismissed();
    if (typeof onSubscribeOverride === "function") {
      onSubscribeOverride(resolvedPrompt);
      return;
    }

    const params = new URLSearchParams({
      src: "event_capacity_limit",
      feature: "event_capacity",
      tier: resolvedPrompt.targetTier || "plus",
      event_id: String(resolvedPrompt.eventId),
    });
    navigate(`/menu/subscription?${params.toString()}`);
  }, [markDismissed, navigate, onSubscribeOverride, resolvedPrompt]);

  if (!visible || !resolvedPrompt) return null;

  const eventName =
    resolvedPrompt.eventName || t("host_capacity_prompt.this_event", { defaultValue: "your event" });
  const title =
    resolvedPrompt.stage === "full"
      ? t("host_capacity_prompt.full_title", { defaultValue: "Your event is full" })
      : t("host_capacity_prompt.near_full_title", {
          defaultValue: "Your event is almost full",
        });
  const bodyKey =
    resolvedPrompt.stage === "full"
      ? "host_capacity_prompt.full_body"
      : "host_capacity_prompt.near_full_body";
  const body = t(bodyKey, {
    eventName,
    participants: resolvedPrompt.participants,
    capacity: resolvedPrompt.capacity,
    remaining: resolvedPrompt.remaining,
    plusLimit: resolvedPrompt.plusLimit,
    premiumLimit: resolvedPrompt.premiumLimit,
  });

  return (
    <AppModalShell
      open={visible}
      title={title}
      onClose={handleClose}
      closeAriaLabel={t("host_capacity_prompt.close", { defaultValue: "Close" })}
      zIndexClassName="z-[13020]"
      maxWidthClassName="max-w-[34rem]"
      titleClassName="text-[22px] sm:text-[28px]"
      footer={
        <div className="grid grid-cols-1 gap-3">
          <AppModalButton onClick={handleSubscribe} className="text-[#2563EB]">
            {t("host_capacity_prompt.cta", { defaultValue: "Increase event capacity" })}
          </AppModalButton>
          <AppModalButton variant="secondary" onClick={handleClose}>
            {t("host_capacity_prompt.later", { defaultValue: "Later" })}
          </AppModalButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2.5">
          <EventCardPreview prompt={resolvedPrompt} eventName={eventName} />
          <CapacityProgress prompt={resolvedPrompt} t={t} />
        </div>
        <p className={APP_MODAL_BODY_TEXT_CLASS}>{body}</p>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <CapacityStat
            label={t("host_capacity_prompt.stat_plus", { defaultValue: "VaultGrid Plus" })}
            value={t("host_capacity_prompt.participants_value", {
              defaultValue: "{{count}} participants",
              count: resolvedPrompt.plusLimit,
            })}
          />
          <CapacityStat
            label={t("host_capacity_prompt.stat_premium", {
              defaultValue: "VaultGrid Premium",
            })}
            value={t("host_capacity_prompt.participants_value", {
              defaultValue: "{{count}} participants",
              count: resolvedPrompt.premiumLimit,
            })}
          />
        </div>
      </div>
    </AppModalShell>
  );
}
